import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CommunityPost } from '../types/community';
import { useAuth } from '../context/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/Community.scss';
import ReportModal from '../components/common/ReportModal';

import icon from '../assets/community.svg';
import icon1 from '../assets/icon_edit.svg';
import view from '../assets/Icon_view.svg';
import back from '../assets/Back_Icon.svg';
import Delete from '../assets/icon_delete.svg';
import Like_Icon from '../assets/Like_Icon.svg';
import Like_Icon_w from '../assets/Like_Icon_w.svg';
import arr_right from '../assets/arr_right.svg';
import moment from 'moment';

const CommunityPostPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<CommunityPost | null>(null);
  const [comment, setComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');

  // 신고 관련 상태
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: 'post' | 'comment', id: string } | null>(null);

  const fetchPost = async () => {
    try {
      const response = await axios.get<CommunityPost>(`/api/community/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [id]);

  const handleCommentSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user || !comment.trim()) return; // 비로그인 또는 빈 댓글 방지

    try {
      await axios.post(`/api/community/${id}/comment`, {
        userId: user.id,
        content: comment,
      });
      setComment(''); // 입력창 비우기
      fetchPost(); // 데이터 다시 불러오기
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleLike = async () => {
    if (!user) return;
    try {
      await axios.post(`/api/community/${id}/like`, { userId: user.id });
      fetchPost();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleCommentLike = async (commentId: string) => {
    if (!user) return;
    try {
      await axios.post(`/api/community/${id}/comment/${commentId}/like`, { userId: user.id });
      fetchPost();
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  const handleCommentEdit = (comment: any) => {
    setEditingCommentId(comment._id);
    setEditingCommentContent(comment.content);
  };

  const handleCommentUpdate = async (commentId: string) => {
    try {
      await axios.put(`/api/community/${id}/comment/${commentId}`, {
        content: editingCommentContent,
      });
      setEditingCommentId(null);
      setEditingCommentContent('');
      fetchPost();
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleCommentDelete = async (commentId: string) => {
    try {
      await axios.delete(`/api/community/${id}/comment/${commentId}`);
      fetchPost();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleDelete = async () => {
    if (!user || user.id !== post?.user?._id) return; // 작성자만 삭제 가능
    try {
      await axios.delete(`/api/community/${id}`);
      navigate('/community');
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  // 신고 버튼 클릭
  const handleReportClick = (type: 'post' | 'comment', targetId: string) => {
    if (!user) {
      alert("로그인이 필요합니다.");
      return;
    }
    setReportTarget({ type, id: targetId });
    setShowReportModal(true);
  };

  // 신고 제출
  const handleReportSubmit = async (reason: string, detail: string) => {
    if (!reportTarget || !user) return;

    try {
      await axios.post('/api/community/reports', {
        reporterId: user.id,
        targetType: reportTarget.type,
        targetId: reportTarget.id,
        reason,
        reasonDetail: detail
      });
      alert("신고가 접수되었습니다.");
      setShowReportModal(false);
    } catch (error: any) {
      console.error('Error submitting report:', error);
      if (error.response && error.response.status === 409) {
        alert("이미 신고한 게시물/댓글입니다.");
      } else {
        alert("신고 접수 중 오류가 발생했습니다.");
      }
    }
  };

  if (!post) return <div>Loading...</div>;

  const isLiked = user && post.likes.includes(user.id);

  return (
    <div className="main-container">
      <div className="community-container app-container community-post-container">
        <div className="page-header">
          <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
          <h2 className="page-header__title">커뮤니티</h2>
        </div>
        <div className="postSection">
          <div className="post-header">
            <div className="header-top">
              <Link to="/community" className="back-to-list-btn">
                <img src={back} alt="목록으로" />
                <p>목록으로</p>
              </Link>

              <div style={{ display: 'flex', gap: '10px' }}>
                {user && user.id === post.user?._id ? (
                  <>
                    <Link to={`/community/edit/${id}`} className="edit-btn">
                      <img src={icon1} alt="수정" />
                      <p>수정</p>
                    </Link>
                    <button onClick={handleDelete} className="delete-btn">
                      <img src={Delete} alt="삭제" />
                      <p>삭제</p>
                    </button>
                  </>
                ) : (
                   user && (
                    <button 
                      onClick={() => id && handleReportClick('post', id)} 
                      className="delete-btn" 
                      style={{ color: '#ff4d4f' }}
                    >
                      신고
                    </button>
                  )
                )}
              </div>
            </div>
            <div className="category">{post.category}</div>
            <h1>{post.title}</h1>
            <div className="meta">
              <span>{post.user?.displayName || '삭제된 사용자'}</span>
              <span>{new Date(post.createdAt).toLocaleDateString()}</span>
              <span className="view">
                <img src={view} alt="조회" /> <p>{post.views}</p>
              </span>
            </div>
          </div>

          <div className="post-content" dangerouslySetInnerHTML={{ __html: post.content }}></div>

          {post.hashtags && post.hashtags.length > 0 && (
            <div className="hashtag-container">
              {post.hashtags.map(tag => (
                <Link to={`/community?tag=${tag.substring(1)}`} key={tag} className="hashtag">
                  {tag}
                </Link>
              ))}
            </div>
          )}

          <div className="post-actions">
            <button onClick={handleLike} className={`like-btn ${isLiked ? 'liked' : ''}`}>
              <img src={isLiked ? Like_Icon_w : Like_Icon} alt="공감" />
              <p>공감 {post.likes.length}</p>
            </button>
          </div>

          <div className="comment-section">
            <h3>
              <img src={arr_right} alt="" />
              댓글 <span>{post.comments.length}</span>
            </h3>
            <ul className="comment-list">
              {post.comments.map(comment => (
                <li key={comment._id} className="comment-item">
                  {editingCommentId === comment._id ? (
                    <div className="comment-edit-form">
                      <ReactQuill
                        theme="snow"
                        value={editingCommentContent}
                        onChange={setEditingCommentContent}
                      />
                      <div className="comment-edit-actions">
                        <button onClick={() => handleCommentUpdate(comment._id)}>저장</button>
                        <button onClick={() => setEditingCommentId(null)}>취소</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="comment-author">
                        {comment.user?.displayName || '삭제된 사용자'}{' '}
                        <p>{moment(comment.createdAt).format('YYYY.MM.DD')}</p>
                      </div>
                      {comment.deleted ? (
                        <div className="comment-content deleted-comment">삭제된 글 입니다</div>
                      ) : (
                        <ReactQuill
                          className="comment-quill-readonly"
                          value={comment.content}
                          readOnly={true}
                          theme="bubble"
                        />
                      )}
                      {!comment.deleted && (
                        <div className="comment-actions">
                          <button
                            onClick={() => handleCommentLike(comment._id)}
                            className="comment-like-btn"
                          >
                            공감 {comment.likes?.length || 0}
                          </button>
                          {user && user.id === comment.user?._id ? (
                            <>
                              <button
                                onClick={() => handleCommentEdit(comment)}
                                className="comment-edit-btn"
                              >
                                수정
                              </button>
                              <button
                                onClick={() => handleCommentDelete(comment._id)}
                                className="comment-delete-btn"
                              >
                                삭제
                              </button>
                            </>
                          ) : (
                            user && (
                              <button 
                                onClick={() => handleReportClick('comment', comment._id)}
                                className="comment-delete-btn"
                                style={{ color: '#ff4d4f' }}
                              >
                                신고
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
            <form className="comment-form" onSubmit={handleCommentSubmit}>
              <ReactQuill theme="snow" value={comment} onChange={setComment} />
              <button type="submit">작성 완료</button>
            </form>
          </div>
        </div>
      </div>
      
      <ReportModal 
        isOpen={showReportModal}
        onConfirm={handleReportSubmit}
        onCancel={() => setShowReportModal(false)}
      />
    </div>
  );
};

export default CommunityPostPage;
